import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ClipboardList,
  Search,
  RefreshCw,
  Loader2,
  User,
  LogIn,
  LogOut,
  ShoppingCart,
  Package,
  Users,
  Settings,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Filter,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(50);
  const [filters, setFilters] = useState({
    action: "",
    user_id: "",
    entity_type: "",
  });
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: skip.toString(),
      });
      
      if (filters.action) params.append("action", filters.action);
      if (filters.user_id) params.append("user_id", filters.user_id);
      if (filters.entity_type) params.append("entity_type", filters.entity_type);
      
      const response = await axios.get(`${API}/api/audit/logs?${params}`);
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, [skip, limit, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, recentRes] = await Promise.all([
        axios.get(`${API}/api/audit/stats`),
        axios.get(`${API}/api/audit/logs/recent?hours=24`)
      ]);
      setStats(statsRes.data);
      setRecentActivity(recentRes.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const getActionIcon = (action) => {
    if (action?.includes("login")) return <LogIn className="h-4 w-4" />;
    if (action?.includes("logout")) return <LogOut className="h-4 w-4" />;
    if (action?.includes("sale")) return <ShoppingCart className="h-4 w-4" />;
    if (action?.includes("product") || action?.includes("stock") || action?.includes("price")) return <Package className="h-4 w-4" />;
    if (action?.includes("customer")) return <Users className="h-4 w-4" />;
    if (action?.includes("user")) return <User className="h-4 w-4" />;
    if (action?.includes("setting") || action?.includes("backup")) return <Settings className="h-4 w-4" />;
    return <ClipboardList className="h-4 w-4" />;
  };

  const getActionColor = (action) => {
    if (action?.includes("failed")) return "bg-red-100 text-red-700 border-red-300";
    if (action?.includes("login")) return "bg-green-100 text-green-700 border-green-300";
    if (action?.includes("logout")) return "bg-gray-100 text-gray-700 border-gray-300";
    if (action?.includes("delete")) return "bg-red-100 text-red-700 border-red-300";
    if (action?.includes("create")) return "bg-blue-100 text-blue-700 border-blue-300";
    if (action?.includes("update") || action?.includes("change")) return "bg-yellow-100 text-yellow-700 border-yellow-300";
    return "bg-gray-100 text-gray-700 border-gray-300";
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "-";
    return new Date(ts).toLocaleString();
  };

  const formatAction = (action) => {
    if (!action) return "-";
    return action.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setSkip(0);
  };

  const clearFilters = () => {
    setFilters({ action: "", user_id: "", entity_type: "" });
    setSkip(0);
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(skip / limit) + 1;

  return (
    <div className="p-6 space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList className="h-6 w-6" />
            Audit Log
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Track all system activities and changes
          </p>
        </div>
        <Button variant="outline" onClick={() => { fetchLogs(); fetchStats(); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {recentActivity && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{recentActivity.total_actions}</p>
                  <p className="text-xs text-gray-500">Last 24 Hours</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <LogIn className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{recentActivity.action_summary?.login || 0}</p>
                  <p className="text-xs text-gray-500">Logins Today</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <ShoppingCart className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{recentActivity.action_summary?.sale_created || 0}</p>
                  <p className="text-xs text-gray-500">Sales Today</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{recentActivity.action_summary?.login_failed || 0}</p>
                  <p className="text-xs text-gray-500">Failed Logins</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            
            <Select
              value={filters.action || "all"}
              onValueChange={(v) => handleFilterChange("action", v === "all" ? "" : v)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="login">Login</SelectItem>
                <SelectItem value="logout">Logout</SelectItem>
                <SelectItem value="login_failed">Failed Login</SelectItem>
                <SelectItem value="sale_created">Sale Created</SelectItem>
                <SelectItem value="product_updated">Product Updated</SelectItem>
                <SelectItem value="price_changed">Price Changed</SelectItem>
                <SelectItem value="stock_adjusted">Stock Adjusted</SelectItem>
                <SelectItem value="customer_created">Customer Created</SelectItem>
                <SelectItem value="user_created">User Created</SelectItem>
                <SelectItem value="backup_created">Backup Created</SelectItem>
              </SelectContent>
            </Select>
            
            <Select
              value={filters.entity_type || "all"}
              onValueChange={(v) => handleFilterChange("entity_type", v === "all" ? "" : v)}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="product">Products</SelectItem>
                <SelectItem value="customer">Customers</SelectItem>
                <SelectItem value="sale">Sales</SelectItem>
                <SelectItem value="user">Users</SelectItem>
              </SelectContent>
            </Select>
            
            {(filters.action || filters.entity_type || filters.user_id) && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
            
            <div className="ml-auto text-sm text-gray-500">
              {total.toLocaleString()} total records
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ClipboardList className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No audit logs found</p>
              <p className="text-sm">Activity will appear here as users interact with the system</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Timestamp</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead className="w-[120px]">IP Address</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log, index) => (
                  <TableRow key={index}>
                    <TableCell className="text-sm text-gray-500">
                      {formatTimestamp(log.timestamp)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`${getActionColor(log.action)} flex items-center gap-1 w-fit`}>
                        {getActionIcon(log.action)}
                        {formatAction(log.action)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{log.user_name || "System"}</span>
                    </TableCell>
                    <TableCell className="text-sm">
                      {log.entity_type && log.entity_id && (
                        <span className="text-gray-500">
                          {log.entity_type}: {log.entity_id.substring(0, 8)}...
                        </span>
                      )}
                      {log.details && (
                        <div className="text-xs text-gray-400 mt-1">
                          {typeof log.details === 'object' 
                            ? Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(', ')
                            : log.details
                          }
                        </div>
                      )}
                      {log.old_value !== undefined && log.new_value !== undefined && (
                        <div className="text-xs mt-1">
                          <span className="text-red-500">{log.old_value}</span>
                          {" → "}
                          <span className="text-green-500">{log.new_value}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-gray-400 font-mono">
                      {log.ip_address || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-500">
            Showing {skip + 1} - {Math.min(skip + limit, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - limit))}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <span className="flex items-center px-3 text-sm">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={skip + limit >= total}
              onClick={() => setSkip(skip + limit)}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLog;
